import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { QBox, QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { AttachmentMeta, Message } from '@captivateiq/assistant/types';
import { ThinkingComplete } from './ThinkingComplete';
import { ThinkingLoader } from './ThinkingLoader';
import { AgentMessage } from './AgentMessage';
import { ErrorMessage } from './ErrorMessage';
import { MessageButtonBar } from './MessageButtonBar';
import { MultipleChoiceOptions } from './MultipleChoiceOptions';
import { UserMessage } from './UserMessage';

import { TabLayout } from '../../TabLayout';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';
import { AgentIntroduction } from '../shared/AgentIntroduction';
import { MessageInput } from '../shared/MessageInput';

export const ChatView = () => {
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const thinkingStartTimeRef = useRef<number | null>(null);
  const [lastThinkingDuration, setLastThinkingDuration] = useState<number | null>(null);

  const {
    selectedAgent,
    currentConversation,
    send,
    agentThinking,
    messageSendError,
    getMessageButtons,
    getMessageAppActions,
    getPanelButtons,
    choices,
    features,
    isExpanded,
    feedbackEnabled,
    messageFeedback,
    messageFeedbackResponse,
    handleFeedback,
    handleFeedbackResponse,
  } = useAIShellContext();

  // Track thinking duration
  useEffect(() => {
    if (agentThinking) {
      thinkingStartTimeRef.current = Date.now();
      setLastThinkingDuration(null);
    } else if (thinkingStartTimeRef.current !== null) {
      const elapsed = Math.round((Date.now() - thinkingStartTimeRef.current) / 1000);
      setLastThinkingDuration(elapsed);
      thinkingStartTimeRef.current = null;
    }
  }, [agentThinking]);

  const messages = useMemo(() => currentConversation?.messages || [], [currentConversation?.messages]);

  const sendAndScroll = useCallback(
    async (message: string, attachments?: AttachmentMeta[]) => {
      if (agentThinking) {
        return;
      }
      bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
      await send(message, attachments?.length ? { attachments } : undefined);
    },
    [send, agentThinking],
  );

  useEffect(() => {
    bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  return (
    <TabLayout bottomContent={<MessageInput agentThinking={agentThinking} onSubmit={sendAndScroll} />}>
      <QBox sx={expandedTextColumnSx(isExpanded)}>
        <AgentIntroduction agentName={selectedAgent ?? ''} />
      </QBox>
      <QStack
        data-testid="ai-chat-view"
        direction="column"
        spacing="10px"
        sx={{
          alignItems: 'stretch',
          backgroundColor: 'grey.50',
          flex: 1,
          padding: '16px',
        }}
      >
        <QBox sx={expandedTextColumnSx(isExpanded)}>
          <MessageButtonBar align="right" buttons={getPanelButtons()} />
        </QBox>
        {messages.map((message: Message, index: number) => {
          const buttons = getMessageButtons(message.id);
          const isLastAgentMessage =
            message.type === 'agent' && !messages.slice(index + 1).some((m) => m.type === 'agent');

          switch (message.type) {
            case 'user':
              return <UserMessage buttons={buttons} key={message.id} message={message} />;
            case 'agent':
              return (
                <React.Fragment key={message.id}>
                  {isLastAgentMessage && !agentThinking && lastThinkingDuration !== null && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <ThinkingComplete elapsedSeconds={lastThinkingDuration} />
                    </QBox>
                  )}
                  <AgentMessage
                    appActions={getMessageAppActions(message.id)}
                    buttons={buttons}
                    feedback={feedbackEnabled ? messageFeedback.get(message.id) ?? null : undefined}
                    feedbackResponse={feedbackEnabled ? messageFeedbackResponse.get(message.id) ?? null : undefined}
                    message={message}
                    onFeedback={feedbackEnabled && !agentThinking ? handleFeedback : undefined}
                    onFeedbackResponse={feedbackEnabled && !agentThinking ? handleFeedbackResponse : undefined}
                  />
                  {isLastAgentMessage && !agentThinking && features.multipleChoice && choices.length > 0 && (
                    <QBox sx={expandedTextColumnSx(isExpanded)}>
                      <MultipleChoiceOptions choices={choices} />
                    </QBox>
                  )}
                </React.Fragment>
              );
            default:
              return null;
          }
        })}
        {agentThinking && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <ThinkingLoader />
          </QBox>
        )}
        {messageSendError && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <ErrorMessage message={messageSendError} />
          </QBox>
        )}
        <div ref={bottomSentinelRef} />
      </QStack>
    </TabLayout>
  );
};
